/**
 * Concierge tasks of the vitrine client (CONCIERGE_CHAT_FIRST.md §6.1). Mirrors the core records
 * (ConciergeViews.ClientRequestView, ConciergeRecords.*). Refusals arrive as `{error: CODE[, field]}` and are
 * localised through `vitrine.error.<CODE>` (mapApiError with the vitrine dialect). Only the creating calls
 * (create, repeat) depend on the concierge mode; the rest work for any existing request.
 */
import { http } from './http';
import type { SourceKind } from './topup';

export type ConciergeCategory = 'EDUCATION' | 'REAL_ESTATE' | 'TRAVEL' | 'MEDICAL' | 'OTHER';

/** Stored status (§5.3) — only what people decide. */
export type ConciergeStoredStatus = 'SUBMITTED' | 'NEEDS_INFO' | 'TERMS_AGREED' | 'CANCELLED' | 'REJECTED';

/**
 * Shown status (§5.3): the stored one, or what the current payment says about it. `COMPLETED` = «Отправлено
 * получателю» — sent, not confirmed by the recipient, and not terminal.
 */
export type ConciergeStatus = ConciergeStoredStatus
  | 'PAYMENT' | 'PAYMENT_FAILED' | 'REFUND_PENDING' | 'COMPLETED';

/** Why the current/last refund happens: the recipient sent it back, or it never left (§6.6). */
export type RefundReason = 'BOUNCED' | 'NOT_SENT';

/**
 * What the client wrote (CONCIERGE_CHAT_FIRST.md §5.1): the task text is canonically `brief.description`; the old
 * structured fields are gone from the client side.
 */
export interface ConciergeBrief {
  description: string | null;
}

/**
 * The client's stage of a task (CONCIERGE_CHAT_FIRST.md §5.6, `clientStage`), as the server names it; the screen
 * shows `vitrine.concierge.stage.<lowerCamel>`. A stage this build does not know gets no pill.
 */
export type ClientStage = 'REVIEW' | 'NEEDS_INFO' | 'AGREED' | 'PREPARING' | 'TO_PAY' | 'CHECKING' | 'SENT'
  | 'FAILED' | 'REFUND' | 'CANCELLED' | 'REJECTED';

/**
 * One row of «Задачи» (§6.1 `GET /concierge-requests`). The server gives the data, the screen picks the title
 * (CF-016). `open` is `isTaskOpen` (CF-034) and drives the «В работе / Завершённые» filter.
 */
export interface ConciergeListItem {
  id: string;
  displayNo: number | string | null;
  /** The recipient of the current terms; null before they are agreed. */
  recipient?: string | null;
  textHead?: string | null;
  firstFileName?: string | null;
  /** The recipient's amount from the terms; null before they are agreed. */
  amount?: number | string | null;
  currency?: string | null;
  /** Null until the manager agrees the terms (CF-002). */
  category?: ConciergeCategory | null;
  /** ISO alpha-3 of the recipient after agreement — the subtitle (§5.1); absent from the §6.1 table, optional here. */
  recipientCountry?: string | null;
  stage: ClientStage | string;
  open: boolean;
  /** The task's support thread — maps unread counters and thread links to the task (02 §3.5). */
  ticketId?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface Beneficiary {
  name: string;
  iban?: string | null;
  accountNumber?: string | null;
  bankName?: string | null;
  swift?: string | null;
  bankCountry: string;
  correspondent?: string | null;
}

export interface InvoiceRef {
  attachmentId: string;
  sha256: string;
}

export interface ConciergeTerms {
  amount: string | number;
  currency: string;
  beneficiary: Beneficiary;
  purpose: string;
  invoices: InvoiceRef[];
}

export interface MethodFields {
  bankCountry?: string | null;
  bankName?: string | null;
  senderType?: string | null;
  accountOwnedByClient?: boolean | null;
  accountLast4?: string | null;
  country?: string | null;
  city?: string | null;
  availableFrom?: string | null;
  availableTo?: string | null;
  comment?: string | null;
}

/** The payment form a manager prefilled (С-7). */
export interface PaymentDraft {
  sourceKind: SourceKind;
  currency: string;
  network?: string | null;
  fields?: MethodFields | null;
}

export interface ConciergeRequestView {
  id: string;
  /** Shown as «TC-<номер>» (CF-024). */
  displayNo?: number | string | null;
  /** Null until the manager agrees the terms (CF-002). */
  category: ConciergeCategory | null;
  status: ConciergeStatus;
  /** The client's label of the task (§5.6 `clientStage`) — the same one the «Задачи» row shows. */
  stage?: ClientStage | string | null;
  brief: ConciergeBrief | null;
  /** The current approved edition only; an edition awaiting the second operator is not shown. */
  agreedTerms: ConciergeTerms | null;
  /** The latest edition waits for a second operator — «Условия обновляются», no payment. */
  termsUpdating: boolean;
  version: number;
  ticketId: string | null;
  rejectReason: string | null;
  openedByManager: boolean;
  createdAt: string;
  updatedAt: string;
  storedStatus: ConciergeStoredStatus;
  /** The current payment as the client sees it; null without one. */
  payment: PaymentClientView | null;
  refundReason: RefundReason | null;
}

/**
 * The refund details an operator entered for the client to confirm (§10.3.6). The account comes masked from the
 * server (`****1234`); the screen never sees the full number.
 */
export interface RefundToClientView {
  version: number;
  kind: 'BANK' | 'CASH';
  holderName: string | null;
  accountMasked: string | null;
  swiftBic: string | null;
  bankName: string | null;
  bankCountry: string | null;
  city: string | null;
  pickupNote: string | null;
  confirmed: boolean;
  confirmedAt: string | null;
}

/** The current payment of a request (ConciergePaymentViews.PaymentClientView, §10.2, §11.1). */
export interface PaymentClientView {
  id: string;
  /** TopUpStatus of the payment row. */
  status: string;
  sourceKind: SourceKind;
  payInCurrency: string;
  payoutAmount: number | string | null;
  payoutCurrency: string | null;
  /** «К оплате» — TH's quote, fee included; null until TH quoted. */
  askAmount: number | string | null;
  askCurrency: string | null;
  partnerFeeAmount: number | string | null;
  feePercent: number | string | null;
  fundingDeadline: string | null;
  /** The requisites a manager released; null when they must not be shown. */
  payInInstructions: string | null;
  markedSent: boolean;
  canMarkSent: boolean;
  cancellable: boolean;
  cancelRequested: boolean;
  refundRequestable: boolean;
  refundRequestedAt: string | null;
  /** TH Payment ID. */
  paymentId: string | null;
  thStage: number | null;
  onHold: boolean;
  refundState: 'NONE' | 'PENDING' | 'DONE';
  refundReason: RefundReason | null;
  refundTo: RefundToClientView | null;
  createdAt: string;
  /** The crypto block (USDT on TRON); null for bank/cash. */
  wallet: ClientWalletView | null;
}

/**
 * Where the crypto payment stands for the client (ConciergeWalletViews.ClientWalletView, §6.4.4, §11.1):
 * WAITING_QUOTE / PREPARING «Готовим реквизиты» · RECALCULATING (the provider's address was revoked, a new quote is
 * coming) · AWAITING (address + amount + deadline) · CHECKING (deadline passed, arrivals still counted until
 * `acceptUntil`) · EXPIRED (new quote / refund) · HOLD (an arrival is being checked) · FORWARDING (on its way to the
 * payment provider) · AT_TH (the provider holds it) · REFUND (being returned).
 */
export type ConciergeWalletPhase = 'WAITING_QUOTE' | 'PREPARING' | 'RECALCULATING' | 'AWAITING' | 'CHECKING'
  | 'EXPIRED' | 'HOLD' | 'FORWARDING' | 'AT_TH' | 'REFUND';

export interface ClientWalletView {
  /** A {@link ConciergeWalletPhase}; a phase this build does not know gets no address and no buttons. */
  phase: ConciergeWalletPhase | string;
  network: string;
  asset: string;
  /** Null until the payment is frozen with a usable address of the provider. */
  address: string | null;
  /** The frozen total, fee included, in `asset` major units. */
  askAmount: number | string | null;
  feeAmount: number | string | null;
  quoteValidUntil: string | null;
  acceptUntil: string | null;
  /** Everything that arrived from the client so far (major units). */
  received: number | string;
  remainingToPay: number | string | null;
  canRequote: boolean;
  canRefundRequest: boolean;
  forwardInFlight: boolean;
  /** «Вернуть» was asked after the address was given (a cancel before that is not a refund). */
  refundRequested: boolean;
  /** A new quote is asked and not frozen yet. */
  requoteRequested: boolean;
}

/** «Вернуть» (crypto before the forward): the address is only a hint — by default the money returns to the sender. */
export interface CryptoRefundRequestBody {
  refundAddress: string | null;
  comment: string | null;
}

/** «Оплатить» (§10.2): the method the client chose; the recipient's amount comes from the agreed terms. */
export interface PaymentBody {
  sourceKind: SourceKind;
  currency: string;
  network: string | null;
  fields: MethodFields;
  /** The request version the screen showed. */
  version: number;
  /** The terms edition whose prefilled form the screen showed; only with a draft. */
  draftVersion: string | null;
}

export interface PaymentCreatedView {
  topupRequestId: string;
  status: string;
}

export interface CreatedView {
  id: string;
  status: ConciergeStatus;
  ticketId: string | null;
}

const ME = '/v1/showcase/me';
const BASE = `${ME}/concierge-requests`;

/** The shared instance times out at 30 s — too short for up to 60 MB over mobile data (see vitrineCard.ts). */
const UPLOAD_TIMEOUT_MS = 300_000;

/**
 * Create a task in one request (CONCIERGE_CHAT_FIRST.md §5.1, §6.1): multipart `text` (optional) + `files` as picked —
 * no compression or re-encoding. Refusals: 400 CONCIERGE_INTAKE_EMPTY, 413 CONCIERGE_INTAKE_TOO_LARGE, 422
 * CONCIERGE_INTAKE_FILE_INVALID; axios sets the multipart boundary itself.
 */
export async function createConciergeRequest(text: string, files: File[]): Promise<CreatedView> {
  const form = new FormData();
  if (text) form.append('text', text);
  for (const f of files) form.append('files', f, f.name);
  return (await http.post<CreatedView>(BASE, form, { timeout: UPLOAD_TIMEOUT_MS })).data;
}

/** The server pages by 20 by default (createdAt desc); the cabinet asks for its maximum page (02 §3.6). */
export const CONCIERGE_PAGE_SIZE = 100;

export async function listConciergeRequests(page = 0, size = CONCIERGE_PAGE_SIZE): Promise<ConciergeListItem[]> {
  return (await http.get<ConciergeListItem[]>(BASE, { params: { page, size } })).data;
}

export async function getConciergeRequest(id: string): Promise<ConciergeRequestView> {
  return (await http.get<ConciergeRequestView>(`${BASE}/${id}`)).data;
}

export async function cancelConciergeRequest(id: string): Promise<void> {
  await http.post(`${BASE}/${id}/cancel`);
}

/** The next tranche: a new request with a copy of the brief only. */
export async function repeatConciergeRequest(id: string): Promise<CreatedView> {
  return (await http.post<CreatedView>(`${BASE}/${id}/repeat`)).data;
}

/**
 * «Оплатить» (§10.2). Conflicts: 409 CONCIERGE_TERMS_CHANGED (re-read the terms), CONCIERGE_DRAFT_CHANGED (re-read
 * the form), CONCIERGE_PAYMENT_BUSY (re-read). The payment's own actions — «Я отправил», cancel, «Запросить возврат» —
 * are the top-up endpoints on `payment.id` (api/topup.ts).
 */
export async function payConciergeRequest(id: string, body: PaymentBody): Promise<PaymentCreatedView> {
  return (await http.post<PaymentCreatedView>(`${BASE}/${id}/payment`, body)).data;
}

/**
 * Crypto «Получить новый расчёт» after the concierge wallet expired (§10.2, §6.4.4 rule 4): a new amount and deadline
 * arrive on the SAME address. 409: CONCIERGE_REQUOTE_NOT_ALLOWED (wallet not expired), CONCIERGE_FUNDS_AT_TH (after the
 * forward), CONCIERGE_ILLEGAL_TRANSITION, CONCIERGE_NOT_CURRENT_PAYMENT.
 */
export async function requoteConciergePayment(id: string): Promise<ConciergeRequestView> {
  return (await http.post<ConciergeRequestView>(`${BASE}/${id}/payment/requote`)).data;
}

/**
 * Crypto «Вернуть» before the forward (§10.2): give up the payment and ask back what arrived. Blank fields go as null —
 * without an address the money returns to the sender. 409 CONCIERGE_FUNDS_AT_TH — the money is on its way to (or at)
 * the payment provider; from there only «Запросить возврат».
 */
export async function requestConciergeCryptoRefund(id: string, body: CryptoRefundRequestBody):
  Promise<ConciergeRequestView> {
  const refundAddress = body.refundAddress?.trim() || null;
  const comment = body.comment?.trim() || null;
  return (await http.post<ConciergeRequestView>(`${BASE}/${id}/payment/refund-request`, { refundAddress, comment })).data;
}

/** The client confirms the refund details the operator entered (§10.3.6); 409 CONCIERGE_REFUND_TO_CHANGED → re-read. */
export async function confirmConciergeRefundTo(id: string, refundToVersion: number): Promise<ConciergeRequestView> {
  return (await http.post<ConciergeRequestView>(`${BASE}/${id}/payment/refund-to/confirm`, { refundToVersion })).data;
}
